// React and Router imports
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Firebase related imports
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, resendEmailVerification, showNotification } from '../../firebase';

// Styles
import "../../css/Auth.css";

function EmailVerification() {
    const [user, loading] = useAuthState(auth);
    const [verificationSent, setVerificationSent] = useState(false);
    const [checking, setChecking] = useState(false);
    const [resending, setResending] = useState(false);
    const navigate = useNavigate();

    const { enhancedRightClickEnabled, updateEmailVerified } = useQuickLinks();
    // Handler for logging out and navigating to login (for context menu)
    const handleContextMenuLogoutAndLogin = async () => {
        try {
            await auth.signOut();
        } catch (err) {
            // Optionally log error
        }
        navigate('/login');
    };

    const contextMenuOptions = [
        { label: "Home", url: "/", onClick: () => navigate("/") },
        { label: "Login", url: "/login", onClick: handleContextMenuLogoutAndLogin },
        ...(user ? [
            { label: "Account Settings", url: `/account/${user.uid}`, onClick: () => navigate(`/account/${user.uid}`) }
        ] : [])
    ];
    const { showContextMenu, ContextMenuComponent } = useContextMenu(contextMenuOptions);

    useEffect(() => {
        if (!loading && !user) {
            navigate('/login');
        }
    }, [user, loading, navigate]);

    const handleResendVerification = async () => {
        setResending(true);
        try {
            const success = await resendEmailVerification();
            if (success) {
                setVerificationSent(true);
            }
        } catch (error) {
            console.error('Error resending verification:', error);
        }
        setResending(false);
    };

    const handleCheckVerification = async () => {
        if (!user) return;
        
        setChecking(true);
        try {
            // Reload user to get fresh verification status
            await user.reload();
            
            if (user.emailVerified) {
                // Also refresh backend status
                const token = await user.getIdToken();
                const response = await fetch('https://bluebirddocumentationadmin.pythonanywhere.com/users/refresh-verification/', {
                    method: 'POST',
                    headers: {
                        'Authorization': `${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (response.ok) {
                    showNotification("Email verified successfully! Welcome to Bluebird Documentation.");
                    // Update context immediately so the banner doesn't reappear on the home page
                    updateEmailVerified(true);
                    navigate('/');
                } else {
                    showNotification("Verification successful, but failed to update backend. Please try refreshing the page.");
                }
            } else {
                showNotification('Email not verified yet. Please check your email and click the verification link.');
            }
        } catch (error) {
            console.error('Error checking verification:', error);
            showNotification('Error checking verification status. Please try again.');
        }
        setChecking(false);
    };

    const handleGoToAccount = () => {
        navigate(`/account/${user.uid}`);
    };

    // Handler for signing out and redirecting to login
    const handleSignOutAndLogin = async (e) => {
        e.preventDefault();
        try {
            await auth.signOut();
        } catch (err) {
            // Optionally log error
        }
        navigate('/login');
    };

    if (loading) {
        return (
            <div className="container-fluid" style={{ padding: "0", minHeight: "100vh", backgroundColor: "#6b9ce7" }}>
                <div className="row" style={{ minHeight: "100vh" }}>
                    <div className="col-12 d-flex justify-content-center align-items-center">
                        <div className="Auth__box">
                            <div style={{ color: 'white', textAlign: 'center' }}>Loading...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return null; // Will redirect to login via useEffect
    }

    return (
        <div 
            className="container-fluid" 
            style={{ padding: "0", minHeight: "100vh", backgroundColor: "#6b9ce7", overflowX: "hidden", overflowY: "hidden" }}
            onContextMenu={enhancedRightClickEnabled ? (e) => {
                e.preventDefault();
                showContextMenu(e.clientX, e.clientY);
            } : undefined}
        >
            <div className="notification"></div>

            <div className="row" style={{ minHeight: "100vh" }}>
                <div className="col-12 d-flex justify-content-center align-items-center">
                    <div className="Auth__box">
                        <h1 className="Auth__text-center"><i style={{ color: "#6b9ce7" }} class="fa-solid fa-envelope-circle-check"></i> Verify Your Email</h1>
                        
                        <div style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
                            <p>We've sent a verification email to:</p>
                            <strong style={{ fontSize: '16px' }}>{user?.email}</strong>
                        </div>
                        
                        <div style={{ color: 'white', textAlign: 'center', lineHeight: '1.5' }}>
                            <p>Please check your email and click the verification link to continue using Bluebird Documentation.</p>
                            <p><small>Don't forget to check your spam/junk folder!</small></p>
                        </div>
                        
                        <button 
                            onClick={handleCheckVerification}
                            disabled={checking}
                            className="Auth__btn"
                            style={{ marginBottom: '15px', width: '100%' }}
                        >
                            {checking ? (
                                <>
                                    <span className="spinner" style={{ marginRight: '10px' }}></span>
                                    Checking...
                                </>
                            ) : (
                                "✅ I've Verified My Email"
                            )}
                        </button>
                        
                        <button 
                            onClick={handleResendVerification}
                            disabled={resending}
                            className="Auth__btn"
                            style={{ 
                                width: '100%',
                                marginBottom: '15px'
                            }}
                        >
                            {resending ? (
                                <>
                                    <span className="spinner" style={{ marginRight: '10px' }}></span>
                                    Sending...
                                </>
                            ) : verificationSent ? (
                                "📨 Verification Email Sent!"
                            ) : (
                                "🔄 Resend Verification Email"
                            )}
                        </button>

                        <button 
                            onClick={handleGoToAccount}
                            className="Auth__btn"
                            style={{ 
                                width: '100%', 
                            }}
                        >
                            ⚙️ Account Settings
                        </button>
                        
                        <div style={{ paddingTop: "20px", color: "white", textAlign: 'center' }}>
                            <small>
                                Need to change your email? Visit your{' '}
                                <Link to={`/account/${user.uid}`} style={{ color: '#ccc', textDecoration: 'underline' }}>
                                    account settings
                                </Link>
                                {' '}first.
                            </small>
                        </div>

                        <div style={{ paddingTop: "15px", color: "white", textAlign: 'center' }}>
                            <small>
                                <a href="#" onClick={handleSignOutAndLogin} style={{ color: '#ccc', textDecoration: 'underline' }}>
                                    Sign in with a different account
                                </a>
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default EmailVerification;
